import React from 'react';
import {  FiTrendingUp,  FiUsers, FiZap,  FiShield, FiMonitor, FiLink } from 'react-icons/fi';

const WebsiteVSERP = () => {
  const integrationBenefits = [
    {
      icon: <FiLink className="w-6 h-6" />,
      title: 'One Platform, One Login, One Experience',
      description: 'When your website and ERP are separate, parents need separate logins for each. They get confused, frustrated, and often give up. Integrated platform means one login for everything—public website, parent portal, student portal, and administrative functions.',
      stat: '70% of parents prefer a single login for all institutional services',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiMonitor className="w-6 h-6" />,
      title: 'Consistent Branding and Design',
      description: 'Your website and parent portal should look like they belong together—same colors, same fonts, same navigation. Separate systems often look completely different, creating confusion and reducing trust.',
      stat: '68% of parents value consistent design across platforms',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Real-Time Data Display on Website',
      description: 'Your ERP knows about admissions, events, announcements, and fee schedules. Your website should display this information in real-time. No more manual updates. No more outdated information.',
      stat: '85% of institutions want real-time data on their website',
      color: 'bg-amber-50'
    },
    {
      icon: <FiZap className="w-6 h-6" />,
      title: 'Reduced Management Complexity',
      description: 'Managing two separate systems—website CMS and ERP—requires double the effort, double the training, and double the cost. One integrated platform simplifies everything.',
      stat: '75% of institutions reduce management time with integrated platform',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Single Data Source for Everything',
      description: 'When website and ERP are separate, data can become inconsistent. Parent information differs between systems. Announcements are posted in one but not the other. Integrated platform means one source of truth.',
      stat: '65% of institutions face data inconsistency issues with separate systems',
      color: 'bg-purple-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Better SEO and User Experience',
      description: 'Integrated platforms often have better SEO capabilities. They generate clean URLs, automatic sitemaps, and optimized meta tags. This helps your website rank higher on Google and attract more parents.',
      stat: 'Websites on integrated platforms rank 40% higher on Google',
      color: 'bg-blue-50'
    }
  ];

  const separateProblems = [
    {
      title: 'Multiple Logins for Parents',
      description: 'Parents must remember different credentials for the website and parent portal. This creates frustration and support calls.'
    },
    {
      title: 'Inconsistent Data',
      description: 'Student information, announcements, and event dates differ between systems. This creates confusion and distrust.'
    },
    {
      title: 'Manual Data Updates',
      description: 'The same information must be entered twice—once in the website CMS, once in the ERP. This is inefficient and error-prone.'
    },
    {
      title: 'Design Inconsistency',
      description: 'Website and portal often look completely different. Parents feel like they are on two different platforms.'
    },
    {
      title: 'Higher Costs',
      description: 'Two systems mean two licenses, two support contracts, and higher overall costs.'
    },
    {
      title: 'Complex Management',
      description: 'Staff must learn and manage two different systems. Training costs are higher. Support is more complicated.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        For most educational institutions, the website and the ERP are two completely separate systems. The website is managed by one team—or sometimes outsourced to an agency. The ERP is managed by another team—or maybe a different vendor. They don't talk to each other. They look different. They have different logins. They create confusion for parents and extra work for staff.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">But what if they were one and the same?</strong> What if your website and your ERP were a single integrated platform? What if parents could use one login to see everything? What if your website displayed real-time data from your ERP?
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's why your institution's website and ERP should be one and the same—and how it transforms the parent experience.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">70%</div>
          <div className="text-sm text-slate-500">Parents prefer a single login</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">85%</div>
          <div className="text-sm text-slate-500">Want real-time data on website</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">40%</div>
          <div className="text-sm text-slate-500">Higher ranking on Google with integration</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Website and ERP Should Be One</h2>

      {/* Benefits List */}
      {integrationBenefits.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Problems with Separate Systems */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">The Problem with Separate Systems</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {separateProblems.map((item, i) => (
            <div key={i} className="bg-red-50 rounded-xl p-4 border border-red-200">
              <div className="flex items-start gap-2">
                <span className="text-red-500 font-bold text-sm">✖️</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Separate Systems vs. Integrated Platform</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Separate Website + ERP</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Multiple logins</span></li>
              <li className="flex items-start gap-2">• <span>Inconsistent design</span></li>
              <li className="flex items-start gap-2">• <span>Manual data updates</span></li>
              <li className="flex items-start gap-2">• <span>Data inconsistency</span></li>
              <li className="flex items-start gap-2">• <span>Higher costs</span></li>
              <li className="flex items-start gap-2">• <span>Complex management</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Integrated with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Single login for all</span></li>
              <li className="flex items-start gap-2">• <span>Consistent design</span></li>
              <li className="flex items-start gap-2">• <span>Real-time data sync</span></li>
              <li className="flex items-start gap-2">• <span>Single source of truth</span></li>
              <li className="flex items-start gap-2">• <span>Lower costs</span></li>
              <li className="flex items-start gap-2">• <span>Simple management</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Parent Experience */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Parent Experience</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          When parents visit your website, they should see the same design and navigation as when they log in to the parent portal. The experience should be seamless. They should feel like they're in the same place.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          Imagine a parent who sees an admission announcement on your website, clicks a button, logs in with their existing credentials, and immediately sees their child's application status. That's an integrated experience. That's what parents expect in 2026.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Separate systems create confusion. Integrated systems build trust.</strong>
        </p>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for Integration</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔗</div>
            <h4 className="font-bold text-slate-900 text-sm">Fully Integrated</h4>
            <p className="text-xs text-slate-600">Website + ERP in one platform</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔑</div>
            <h4 className="font-bold text-slate-900 text-sm">Single Login</h4>
            <p className="text-xs text-slate-600">One login for all users</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎨</div>
            <h4 className="font-bold text-slate-900 text-sm">Consistent Design</h4>
            <p className="text-xs text-slate-600">Same look and feel everywhere</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Data</h4>
            <p className="text-xs text-slate-600">Always up-to-date</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Secure</h4>
            <p className="text-xs text-slate-600">Enterprise-grade security</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Cost Effective</h4>
            <p className="text-xs text-slate-600">One subscription, all features</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Stop Managing Two Systems with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Your institution's website and ERP should work together, not separately. Separate systems create confusion, inconsistency, and extra work. Integrated platforms create seamless experiences, consistent data, and simplified management.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon gives you both—a powerful public website and a comprehensive educational ERP. All in one platform. Consistent branding. Seamless experience. One subscription. One login. One system.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for integrated website + ERP.</strong> One platform. All the features. Seamless experience.
        </p>
      </div>
    </>
  );
};

export default WebsiteVSERP;